<script lang="ts" setup>
  import { computed } from 'vue';

  import { toSystemColor } from '@bv/utils/common/color/to-system-color';
  import { trans } from '@bv/utils/i18n';

  const props = withDefaults(
    defineProps<{
      label?: string;
      color?: string;
      rounded?: string;
      labelSize?: string;
      borderColor?: string;
      borderWidth?: string;
      labelColor?: string;
      bgColor?: string;
      class?: string | Record<string, boolean>;
    }>(),
    {
      label: undefined,
      color: 'gray',
      rounded: '0.5rem',
      labelSize: '1rem',
      borderColor: undefined,
      borderWidth: '1px',
      labelColor: undefined,
      bgColor: undefined,
      class: undefined,
    },
  );

  const colors = computed(() => ({
    color: toSystemColor(props.color),
    labelColor: toSystemColor(props.labelColor || props.color),
    borderColor: toSystemColor(props.borderColor || props.color),
    bgColor: toSystemColor(props.bgColor || 'transparent'),
  }));
</script>

<template>
  <div
    class="bv-box"
    :class="props.class"
  >
    <div class="box-label relative">
      <div class="label-outline absolute inset-0 flex">
        <div class="label-outline-start" />
        <div class="label-outline-notch">
          <label v-if="label || $slots.label">
            <slot
              v-if="$slots.label"
              name="label"
            />
            <template v-else-if="label">
              {{ trans(props.label || '') }}
            </template>
          </label>
        </div>
        <div class="label-outline-end grow" />
      </div>
      <div class="content relative">
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .bv-box {
    --bv-box-bg-color: v-bind('colors.bgColor');
    --bv-box-label-color: v-bind('colors.labelColor');
    --bv-box-border-color: v-bind('colors.borderColor');
    --bv-box-border-width: v-bind(borderWidth);
    --bv-box-radius: v-bind(rounded);
    --bv-box-label-font-size: v-bind(!!label ? labelSize: 0);

    min-height: var(--bv-box-min-height);
    display: flex;
    flex-direction: column;

    .box-label {
      display: flex;
      flex: 1;
      flex-direction: column;

      .content {
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: center;
        padding-top: var(--bv-box-padding-top);
        padding-bottom: var(--bv-box-padding-bottom);
        padding-inline-start: var(--bv-box-padding-inline-start);
        padding-inline-end: var(--bv-box-padding-inline-end);
        color: inherit;
      }

      .label-outline {
        color: var(--bv-box-border-color);

        .label-outline-start {
          width: max(var(--bv-box-padding-inline-start), var(--bv-box-radius));
          border-top-width: var(--bv-box-border-width);
          border-bottom-width: var(--bv-box-border-width);
          border-inline-start-width: var(--bv-box-border-width);
          border-start-start-radius: var(--bv-box-radius);
          border-end-start-radius: var(--bv-box-radius);
          background-color: var(--bv-box-bg-color);
        }

        .label-outline-notch {
          border-bottom-width: var(--bv-box-border-width);

          label {
            display: block;
            top: -50%;
            transform: translateY(-50%);
            padding: var(--bv-box-label-padding);
            color: var(--bv-box-label-color);
            font-size: var(--bv-box-label-font-size);
            line-height: calc(var(--bv-box-label-font-size) * 1.125 / 0.875);
            background-color: var(--bv-box-label-bg-color);
          }

          background-color: var(--bv-box-bg-color);
        }

        .label-outline-end {
          border-top-width: var(--bv-box-border-width);
          border-bottom-width: var(--bv-box-border-width);
          border-inline-end-width: var(--bv-box-border-width);
          border-start-end-radius: var(--bv-box-radius);
          border-end-end-radius: var(--bv-box-radius);
          background-color: var(--bv-box-bg-color);
        }
      }
    }
  }
</style>
